import { StyledPartners } from './style';
import { Button, Checkbox, Input, Table, Tabs, Dropdown } from 'ui';
import { useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import { message, MenuProps } from 'antd';
import { useDebounce } from 'utils/useDebounce';
import useQueryApiClient from 'utils/useQueryApiClient';
import DefaultLayout from 'admin/components/DefaultLayout';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { StyledMobilePageTitle } from 'admin/components/MobileLayout/style';

interface FarmsProps {
  reg_nr: number;
  name: string;
  id: number;
}

interface PartnerProps {
  confirmed: ConfirmedStatus;
  confirmed_at: string;
  created_at: string;
  farm_id: number;
  id: number;
  name: string;
  partner_id: number;
  reg_nr: number;
  updated_at: string;
  is_inbound?: boolean;
}

type ConfirmedStatus = 0 | 1 | 2;

type StatusKey = 'canceled' | 'accepted' | 'waiting';

const StringStatus: { [key in ConfirmedStatus]: StatusKey } = {
  0: 'canceled',
  1: 'accepted',
  2: 'waiting',
};

interface PartnersComponentProps {
  farmId?: number;
  showPageLayout?: boolean;
}

const Partners: React.FC<PartnersComponentProps> = ({
  farmId,
  showPageLayout = false,
}) => {
  const [search, setSearch] = useState('');
  const [reloadCount, setReloadCount] = useState<number>(0);
  const intl = useIntl();

  const stableFilter = useMemo(() => {
    return farmId ? { farm_id: farmId } : undefined;
  }, [farmId]);

  const { appendData: deletePartner } = useQueryApiClient({
    request: {
      url: '/api/v2/partners/:id',
      method: 'DELETE',
    },
    onSuccess: (response) => handleSuccess(response, 'delete'),
  });

  const { appendData: acceptPartner } = useQueryApiClient({
    request: {
      url: '/api/v2/partners/accept/:partner',
      method: 'POST',
    },
    onSuccess: (response) => handleSuccess(response, 'accept'),
  });

  const { appendData: cancelPartner } = useQueryApiClient({
    request: {
      url: '/api/v2/partners/cancel/:partner',
      method: 'POST',
    },
    onSuccess: (response) => handleSuccess(response, 'cancel'),
  });

  const { appendData: invitePartner } = useQueryApiClient({
    request: {
      url: '/api/v2/partners/invite',
      method: 'POST',
    },
    onSuccess: (response) => handleSuccess(response, 'invite'),
  });
  const stableSearch = useDebounce({ search: search }, 1000);

  const stableFilterObject = useMemo(() => stableSearch, [stableSearch.search]);

  const handleSuccess = (
    response: any,
    action: 'invite' | 'accept' | 'cancel' | 'delete'
  ) => {
    if (response.error) {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    } else {
      switch (action) {
        case 'invite':
          message.success(
            intl.formatMessage({ id: 'message.successfully_invited_partner' })
          );
          break;
        case 'accept':
          message.success(
            intl.formatMessage({ id: 'message.successfully_accepted_partner' })
          );
          break;
        case 'cancel':
          message.success(
            intl.formatMessage({ id: 'message.successfully_canceled_partner' })
          );
          break;
        case 'delete':
          message.success(
            intl.formatMessage({ id: 'message.successfully_deleted_partner' })
          );
          break;
      }

      setReloadCount((p) => p + 1);
    }
  };

  const handleAcceptPartnership = (recordId: number) => {
    acceptPartner(stableFilter, { partner: recordId });
  };

  const handleCancelPartnership = (recordId: number) => {
    cancelPartner(stableFilter, { partner: recordId });
  };

  const handleDelete = (partnerId: number) => {
    if (
      window.confirm(
        intl.formatMessage({ id: 'message.confirm_delete_partnership' })
      )
    ) {
      deletePartner(null, { id: partnerId });
    }
  };

  const handleInvite = (farmId: number) => {
    if (
      window.confirm(
        intl.formatMessage({ id: 'message.invite_partner_confirm' })
      )
    ) {
      invitePartner({ ...stableFilter, partner_id: farmId });
    }
  };

  const RenderActions = (record: PartnerProps) => {
    const states: { [key in StatusKey]: React.ReactNode } = {
      canceled: intl.formatMessage({ id: 'general.canceled' }),
      accepted: (
        <Button
          type={'text'}
          label={intl.formatMessage({ id: 'general.delete' })}
          onClick={() => handleDelete(record.id)}
          className={'underline'}
          ability="edit_partner"
        />
      ),
      waiting: intl.formatMessage({ id: 'general.waiting' }),
    };

    if (record.is_inbound && record.confirmed === 2) {
      return (
        <div className={'inbound'}>
          <Button
            label={intl.formatMessage({ id: 'general.accept' })}
            type={'primary'}
            onClick={() => handleAcceptPartnership(record.id)}
            ability="edit_partner"
          />
          <Button
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => handleCancelPartnership(record.id)}
            ability="edit_partner"
          />
        </div>
      );
    }

    return states[StringStatus[record.confirmed]];
  };

  const searchColumns = [
    {
      title: intl.formatMessage({ id: 'general.name' }),
      dataIndex: 'name',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'general.reg_nr' }),
      dataIndex: 'reg_nr',
      render: (value: number) => value,
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      render: (record: FarmsProps) => {
        return (
          <Button
            type={'primary'}
            label={intl.formatMessage({ id: 'general.invite' })}
            onClick={() => handleInvite(record.id)}
            ability="edit_partner"
          />
        );
      },
    },
  ];

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.name' }),
      dataIndex: 'name',
      render: (value: string) => value,
    },

    {
      title: intl.formatMessage({ id: 'general.reg_nr' }),
      dataIndex: 'reg_nr',
      render: (value: number) => value,
    },

    {
      title: intl.formatMessage({ id: 'general.status' }),
      dataIndex: 'confirmed',
      render: (value: number) => (
        <Checkbox checked={value === 1} disabled={true} />
      ),
      className: 'centered-column',
    },

    {
      title: (
        <div className={'actions-title'}>
          {intl.formatMessage({ id: 'general.actions' })}
        </div>
      ),
      render: (record: PartnerProps) => {
        return <div className={'actions-wrap'}>{RenderActions(record)}</div>;
      },
    },
  ];

  const mobileItems = {
    coverFields: [
      {
        name: 'name',
        render: (value: string) => value,
      },
      {
        name: 'reg_nr',
        render: (value: number) => value,
      },
    ],
    bodyFields: [
      {
        name: 'confirmed',
        label: intl.formatMessage({ id: 'general.status' }),
        render: (value: number) => (
          <Checkbox checked={value === 1} disabled={true} />
        ),
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (_: undefined, record: PartnerProps) => {
          const items: MenuProps['items'] = [];

          if (record.is_inbound && record.confirmed === 2) {
            items.push(
              {
                key: 'accept',
                label: intl.formatMessage({ id: 'general.accept' }),
              },
              {
                key: 'cancel',
                label: intl.formatMessage({ id: 'general.cancel' }),
              }
            );
          } else if (record.confirmed === 1) {
            items.push({
              key: 'delete',
              label: intl.formatMessage({ id: 'general.delete' }),
            });
          }

          const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
            switch (key) {
              case 'accept':
                handleAcceptPartnership(record.id);
                break;
              case 'cancel':
                handleCancelPartnership(record.id);
                break;
              case 'delete':
                handleDelete(record.id);
                break;
            }
          };

          if (items.length === 0) {
            return (
              <div className={'actions-wrap'}>{RenderActions(record)}</div>
            );
          }

          return (
            <Dropdown
              menu={{ items, onClick: handleMenuClick }}
              trigger={['click']}
              children={
                <Button label={intl.formatMessage({ id: 'general.actions' })} />
              }
            />
          );
        },
      },
    ],
  };

  const searchMobileItems = {
    coverFields: [
      {
        name: 'name',
        render: (value: string) => value,
      },
      {
        name: 'reg_nr',
        render: (value: number) => value,
      },
    ],
    bodyFields: [
      {
        name: '_',
        render: () => {
          return <div></div>;
        },
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (_: undefined, record: FarmsProps) => {
          return (
            <Button
              type={'primary'}
              label={intl.formatMessage({ id: 'general.invite' })}
              onClick={() => handleInvite(record.id)}
            />
          );
        },
      },
    ],
  };

  const items = [
    {
      key: 'partners',
      label: intl.formatMessage({ id: 'navigation.partners' }),
      children: (
        <>
          <div className={'partners'}>
            <Input
              label={intl.formatMessage({ id: 'general.invite_partner' })}
              name={'search'}
              layout={'vertical'}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className={'under-text'}>
              {intl.formatMessage({ id: 'general.add_farm_name_or_reg_nr' })}
            </span>
          </div>
          {!!stableSearch.search && (
            <ResponsiveWrapper
              desktopComponent={
                <Table
                  columns={searchColumns}
                  url={'/api/v2/partners/farms'}
                  filter={stableFilterObject}
                />
              }
              mobileComponent={
                <MobileList
                  url={'/api/v2/partners/farms'}
                  coverFields={searchMobileItems.coverFields}
                  bodyFields={searchMobileItems.bodyFields}
                  footerFields={searchMobileItems.footerFields}
                  filter={stableFilterObject}
                />
              }
            />
          )}
          <div className={'separator'} />
          <ResponsiveWrapper
            desktopComponent={
              <Table
                url={'/api/v2/partners'}
                columns={columns}
                reload={reloadCount}
                filter={stableFilter}
                page_size={10}
              />
            }
            mobileComponent={
              <MobileList
                url={'/api/v2/partners'}
                coverFields={mobileItems.coverFields}
                bodyFields={mobileItems.bodyFields}
                footerFields={mobileItems.footerFields}
                reload={reloadCount}
                filter={stableFilter}
                page_size={10}
              />
            }
          />
        </>
      ),
    },
  ];

  return (
    <StyledPartners className={!showPageLayout ? 'hide-page-layout' : ''}>
      <DefaultLayout.PageLayout
        breadcrumbs={
          showPageLayout
            ? [
                {
                  title: intl.formatMessage({ id: 'navigation.beginning' }),
                  href: '/',
                },
                {
                  title: intl.formatMessage({ id: 'navigation.partners' }),
                },
              ]
            : undefined
        }
      >
        <DefaultLayout.PageContent>
          <StyledMobilePageTitle>
            <span>{intl.formatMessage({ id: 'navigation.partners' })}</span>
          </StyledMobilePageTitle>
          <Tabs items={items} />
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </StyledPartners>
  );
};

export default Partners;
